import { useEffect, useState } from "react";
import api from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { SeverityIcon } from "@/components/SeverityIcon";
import { format, formatDistanceToNow } from "date-fns";
import { ArrowsClockwise, PaperPlaneTilt, WarningOctagon, Pulse, Clock, Users, MapPin, ShieldCheck, ChartLine } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const StatCard = ({ label, value, accent, icon: Icon, testid }) => (
  <div className="tac-card p-4 flex flex-col gap-3" data-testid={testid}>
    <div className="flex items-center justify-between">
      <div className="label-mono">{label}</div>
      <Icon size={16} className="text-zinc-500" weight="duotone" />
    </div>
    <div className={`font-display text-4xl font-black tracking-tight ${accent || "text-white"}`}>
      {value}
    </div>
  </div>
);

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [shifts, setShifts] = useState([]);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ticking, setTicking] = useState(false);
  const [sendingReport, setSendingReport] = useState(false);

  const load = async () => {
    try {
      const [s, sh, act] = await Promise.all([
        api.get("/dashboard/stats"),
        api.get("/shifts"),
        api.get("/activity?limit=30"),
      ]);
      setStats(s.data);
      setShifts(sh.data);
      setActivity(act.data);
    } catch (e) {
      console.error("Dashboard.load failed", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const initial = setTimeout(load, 0);
    const id = setInterval(load, 8000);
    return () => { clearTimeout(initial); clearInterval(id); };
  }, []);

  const handleTick = async () => {
    setTicking(true);
    try {
      const { data } = await api.post("/scheduler/tick");
      toast.success(data.mocked ? "Scheduler ticked (MOCKED SMS)" : "Scheduler ticked");
      await load();
    } catch (e) {
      toast.error("Tick failed");
    } finally {
      setTicking(false);
    }
  };

  const handleSendCheckin = async (shiftId) => {
    try {
      await api.post(`/shifts/${shiftId}/send-checkin`);
      toast.success("Check-in ping sent");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send ping");
    }
  };

  const handleSendWeeklyReport = async () => {
    setSendingReport(true);
    try {
      const { data } = await api.post("/reports/weekly/send?days=7");
      if (data.sent > 0) {
        toast.success(`Weekly recap texted to ${data.sent} dispatcher(s)`, {
          description: data.preview,
          duration: 10000,
        });
      } else {
        toast.message("Report generated but no dispatchers had alerts enabled", {
          description: data.preview,
          duration: 10000,
        });
      }
    } catch (e) {
      toast.error("Weekly report failed");
    } finally {
      setSendingReport(false);
    }
  };

  const liveShifts = shifts
    .filter((s) => ["scheduled", "ping_sent", "late", "arrived"].includes(s.status))
    .slice(0, 8);

  const criticalAlerts = shifts.filter((s) => ["no_show", "cant_make_it"].includes(s.status)).slice(0, 5);

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-mono">// COMMAND CENTER</div>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight mt-1">
            Dispatch Board
          </h1>
          <p className="text-sm text-zinc-500 mt-1 max-w-xl">
            Sandy auto-texts guards before each shift. Replies (YES / LATE / NO) update
            status here in real time, with escalation when a post goes silent.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            data-testid="send-weekly-report-btn"
            onClick={handleSendWeeklyReport}
            disabled={sendingReport}
            className="flex items-center gap-2 border border-[#2BD4D9]/40 text-[#2BD4D9] hover:bg-[#2BD4D9]/10 px-3 py-2 text-xs uppercase tracking-wider disabled:opacity-40"
          >
            <ChartLine size={14} weight="bold" /> {sendingReport ? "Sending..." : "Weekly Recap"}
          </button>
          <button
            data-testid="scheduler-tick-btn"
            onClick={handleTick}
            disabled={ticking}
            className="flex items-center gap-2 border border-white/15 hover:border-white/30 hover:bg-white/5 px-3 py-2 text-xs uppercase tracking-wider"
          >
            <Pulse size={14} weight="bold" /> {ticking ? "Pulsing..." : "Run Scheduler"}
          </button>
          <button
            data-testid="refresh-btn"
            onClick={load}
            className="flex items-center gap-2 border border-white/15 hover:border-white/30 hover:bg-white/5 px-3 py-2 text-xs uppercase tracking-wider"
          >
            <ArrowsClockwise size={14} weight="bold" /> Refresh
          </button>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10">
        <StatCard label="On Post" value={stats?.arrived ?? "—"} accent="text-emerald-400" icon={ShieldCheck} testid="stat-arrived" />
        <StatCard label="Awaiting Reply" value={stats?.pending_response ?? "—"} accent="text-blue-400" icon={Clock} testid="stat-pending" />
        <StatCard label="Late" value={stats?.late ?? "—"} accent="text-yellow-400" icon={WarningOctagon} testid="stat-late" />
        <StatCard label="No-Show" value={stats?.no_show ?? "—"} accent="text-red-400" icon={WarningOctagon} testid="stat-noshow" />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-white/10">
        <StatCard label="Scheduled Today" value={stats?.scheduled_today ?? "—"} icon={Clock} testid="stat-today" />
        <StatCard label="Total Guards" value={stats?.total_guards ?? "—"} icon={Users} testid="stat-guards" />
        <StatCard label="Active Sites" value={stats?.total_sites ?? "—"} icon={MapPin} testid="stat-sites" />
      </div>

      {/* Critical alerts banner */}
      {criticalAlerts.length > 0 && (
        <div className="tac-card border-red-500/40 bg-red-500/5 p-4" data-testid="critical-alerts">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-2 h-2 bg-red-500 pulse-critical rounded-full" />
            <div className="label-mono text-red-400">CRITICAL ALERTS · {criticalAlerts.length}</div>
          </div>
          <div className="space-y-2">
            {criticalAlerts.map((s) => (
              <div key={s.shift_id} className="flex items-center justify-between text-sm">
                <span className="text-red-200">
                  <span className="font-semibold">{s.guard_name}</span> · {s.site_name} ·{" "}
                  <span className="font-mono text-xs">{format(new Date(s.start_time), "HH:mm")}</span>
                </span>
                <StatusBadge status={s.status} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Live ops grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 tac-card">
          <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
            <div className="label-mono">// LIVE SHIFT BOARD</div>
            <Link to="/shifts" className="text-xs text-zinc-400 hover:text-white" data-testid="see-all-shifts">
              ALL SHIFTS →
            </Link>
          </div>
          {loading ? (
            <div className="p-8 text-center text-zinc-500 label-mono">LOADING...</div>
          ) : liveShifts.length === 0 ? (
            <div className="p-10 text-center">
              <div className="label-mono mb-2">// NO ACTIVE SHIFTS</div>
              <p className="text-sm text-zinc-400">
                Create a guard, a site, then schedule a shift to see the dispatch board come alive.
              </p>
              <Link to="/shifts" className="inline-block mt-4 px-3 py-2 border border-white/15 hover:bg-white/5 text-xs uppercase tracking-wider" data-testid="create-first-shift">
                Create First Shift
              </Link>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-zinc-500">
                <tr className="border-b border-white/5">
                  <th className="text-left px-4 py-2 label-mono">Guard</th>
                  <th className="text-left px-4 py-2 label-mono">Site</th>
                  <th className="text-left px-4 py-2 label-mono">Start</th>
                  <th className="text-left px-4 py-2 label-mono">Status</th>
                  <th className="text-right px-4 py-2 label-mono">Action</th>
                </tr>
              </thead>
              <tbody>
                {liveShifts.map((s) => (
                  <tr key={s.shift_id} className="border-b border-white/5 hover:bg-white/[0.03]">
                    <td className="px-4 py-3">
                      <div className="font-medium">{s.guard_name}</div>
                      <div className="text-xs text-zinc-500 font-mono">{s.guard_phone || "—"}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div>{s.site_name}</div>
                      <div className="text-xs text-zinc-500">{s.site_address}</div>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-zinc-300">
                      {format(new Date(s.start_time), "MMM d · HH:mm")}
                    </td>
                    <td className="px-4 py-3"><StatusBadge status={s.status} /></td>
                    <td className="px-4 py-3 text-right">
                      <button
                        data-testid={`send-ping-${s.shift_id}`}
                        onClick={() => handleSendCheckin(s.shift_id)}
                        className="inline-flex items-center gap-1 border border-white/15 hover:border-[#007AFF] hover:text-[#007AFF] px-2 py-1 text-[0.65rem] uppercase tracking-wider"
                      >
                        <PaperPlaneTilt size={12} /> Ping
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="tac-card">
          <div className="px-4 py-3 border-b border-white/10 label-mono">// ACTIVITY FEED</div>
          <div className="max-h-[520px] overflow-auto">
            {activity.length === 0 ? (
              <div className="p-6 text-zinc-500 text-sm text-center">No activity yet.</div>
            ) : (
              activity.map((a) => (
                <div key={a.log_id} className="px-4 py-3 border-b border-white/5 last:border-0">
                  <div className="flex items-center gap-2 mb-1">
                    <SeverityIcon severity={a.severity} />
                    <span className="label-mono">{a.type.replace(/_/g, " ")}</span>
                  </div>
                  <p className="text-xs text-zinc-300 leading-relaxed">{a.message}</p>
                  <div className="text-[0.65rem] text-zinc-600 font-mono mt-1">
                    {formatDistanceToNow(new Date(a.timestamp), { addSuffix: true })}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
